import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import BLOG_POSTS from './blogData';
import './PublicPages.css';

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find(p => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!post) {
    return (
      <div className="public-page">
        <PublicHeader />
        <main className="public-main">
          <div className="public-container" style={{ textAlign: 'center', padding: '80px 20px' }}>
            <h1 style={{ color: '#fff', fontSize: '32px', marginBottom: '16px' }}>Post Not Found</h1>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '24px' }}>This blog post doesn't exist.</p>
            <Link to="/blog" className="btn btn-primary">Back to Blog</Link>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  const postIndex = BLOG_POSTS.findIndex(p => p.slug === slug);
  const prevPost = postIndex > 0 ? BLOG_POSTS[postIndex - 1] : null;
  const nextPost = postIndex < BLOG_POSTS.length - 1 ? BLOG_POSTS[postIndex + 1] : null;

  return (
    <div className="public-page">
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.description,
            datePublished: post.date,
            dateModified: post.date,
            author: { '@type': 'Organization', name: 'Hangrily', url: 'https://hangrily.app' },
            publisher: {
              '@type': 'Organization',
              name: 'Hangrily',
              logo: { '@type': 'ImageObject', url: 'https://hangrily.app/logo512.png' }
            },
            image: post.image,
            mainEntityOfPage: `https://hangrily.app/blog/${post.slug}`,
            keywords: post.tags.join(', ')
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://hangrily.app/blog' },
              { '@type': 'ListItem', position: 3, name: post.title, item: `https://hangrily.app/blog/${post.slug}` }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="public-main">
        <div className="blog-post-container">
          <Link to="/blog" className="blog-back-link">&larr; Back to Blog</Link>

          <article className="blog-article">
            <div className="blog-post-header">
              <span className="blog-post-category">{post.category}</span>
              <h1 className="blog-post-title">{post.title}</h1>
              <div className="blog-post-meta">
                <span>{post.author}</span>
                <span className="blog-meta-dot">&middot;</span>
                <span>{post.date}</span>
                <span className="blog-meta-dot">&middot;</span>
                <span>{post.readTime}</span>
              </div>
            </div>

            {post.image && (
              <div className="blog-post-hero-image" style={{ backgroundImage: `url(${post.image})` }} />
            )}

            <div className="blog-post-body">
              {post.content.map((block, i) => {
                if (block.type === 'paragraph') {
                  return <p key={i} className="blog-p">{block.text}</p>;
                }
                if (block.type === 'heading') {
                  return <h2 key={i} className="blog-h2">{block.text}</h2>;
                }
                if (block.type === 'list') {
                  return (
                    <ul key={i} className="blog-list">
                      {block.items.map((item, j) => (
                        <li key={j} dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  );
                }
                if (block.type === 'cta') {
                  return (
                    <div key={i} className="blog-cta-box">
                      <p>{block.text}</p>
                      <Link to={block.link} className="btn btn-primary">Try Hangrily Free</Link>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="blog-tags">
              {post.tags.map(tag => (
                <span key={tag} className="blog-tag">{tag}</span>
              ))}
            </div>
          </article>

          <nav className="blog-nav">
            {prevPost ? (
              <Link to={`/blog/${prevPost.slug}`} className="blog-nav-link blog-nav-prev">
                <span className="blog-nav-label">&larr; Previous</span>
                <span className="blog-nav-title">{prevPost.title}</span>
              </Link>
            ) : <div />}
            {nextPost ? (
              <Link to={`/blog/${nextPost.slug}`} className="blog-nav-link blog-nav-next">
                <span className="blog-nav-label">Next &rarr;</span>
                <span className="blog-nav-title">{nextPost.title}</span>
              </Link>
            ) : <div />}
          </nav>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
